<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div v-if="visible" class="modal-overlay">
        <Transition name="modal-scale">
          <div class="modal-content">
            <div class="modal-header">
              <div class="modal-title">{{ $t("message.select_payment_method") }}</div>
              <div class="close-btn" @click="handleClose">×</div>
            </div>

            <div class="payment-options">
              <div v-for="(option, index) in paymentOptions" :key="index" :class="[
                'payment-option',
                selectedPayment === option.id ? 'selected' : '',
              ]" @click="selectPayment(option.id)">
                <div class="payment-icon">
                  <img :src="option.icon" :alt="option.name" />
                </div>
                <div class="payment-name">{{ option.name }}</div>
                <div class="payment-check">
                  <div class="check-circle" :class="{ active: selectedPayment === option.id }"></div>
                </div>
              </div>
            </div>

            <div class="payment-footer">
              <div class="payment-amount">
                <span class="label">{{ $t("message.total_amount") }}</span>
                <span class="amount">{{ amount }} TON</span>
              </div>
              <button class="pay-btn" @click="handlePay" :disabled="isLoading">
                {{ $t("message.next_step") }}
              </button>
            </div>

            <!-- 支付状态提示 -->
            <Transition name="fade">
              <div v-if="paymentStatus" class="payment-status-overlay">
                <div class="payment-status-content">
                  <div v-if="isLoading" class="loading-spinner"></div>
                  <span>{{ paymentStatus }}</span>
                </div>
              </div>
            </Transition>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import emitter from "@/utils/bus";
import ton from "@/assets/payment-icon/ton.svg";
import stars from "@/assets/payment-icon/stars.svg";
import googlePay from "@/assets/payment-icon/google.svg";
import applePay from "@/assets/payment-icon/apple.svg";
import usdt from "@/assets/payment-icon/usdt.svg";
import { useTonConnectStore } from "@/store/tonConnect";
import { appHapticFeedback, appHapticFeedbackSoft, openExternalLink } from "@/utils/common";
import { useUserStore } from "@/store/user";
import { useI18n } from "vue-i18n";
import { useConfigStore } from "@/store/config";

const { t } = useI18n();
const config = useConfigStore();

const user_store = useUserStore();

const themeColor = computed(() => {
  return config.themes_color;
});

const visible = ref(false); // 是否显示
const amount = ref(0); // 金额
const tonConnectStore = useTonConnectStore();
const selectedPayment = ref("stars"); // 选择支付方式
const isMode = ref(false); // 是否是弹窗
const isLoading = ref(false); // 加载状态
const paymentStatus = ref(""); // 支付状态提示
const isAndroid = () => /android/i.test(navigator.userAgent);
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isBuyMinigMachine = ref(false); // 是否购买矿机

const funStart = ref(null); // 星星订单
const funTON = ref(null); // TON订单
const funUSDT = ref(null); // USDT订单
let params = null; // 参数

const paymentOptions = computed(() => {
  const options = [];

  if (isAndroid()) {
    options.push({
      id: "stars",
      name: "Google Pay",
      icon: googlePay,
    });
  } else if (isIOS()) {
    options.push({
      id: "stars",
      name: "Apple Pay",
      icon: applePay,
    });
  } else {
    options.push({
      id: "stars",
      name: "Stars",
      icon: stars,
    });
  }

  options.push({
    id: "ton",
    name: "TON",
    icon: ton,
  });

  options.push({
    id: "usdt",
    name: "USDT",
    icon: usdt,
  });

  return options;
});

const selectPayment = (id) => {
  appHapticFeedbackSoft();
  selectedPayment.value = id;
};

const handlePay = async () => {
  appHapticFeedback();
  isLoading.value = true;
  paymentStatus.value = t("message.creating_order");

  try {
    switch (selectedPayment.value) {
      case "stars":
        let starsRes;
        starsRes = await funStart.value(params);
        if (starsRes.code === 200) {
          paymentStatus.value = t("message.opening_payment");
          // 浏览器环境：直接打开支付链接
          openExternalLink(starsRes.data.url);
          // 浏览器中无法监听支付状态，提示用户支付完成后刷新
          paymentStatus.value = t("message.payment_pending"); // 等待支付
          setTimeout(() => {
            paymentStatus.value = "";
          }, 3000);
        } else {
          paymentStatus.value = t("message.create_order_failed");
          setTimeout(() => {
            paymentStatus.value = "";
            isLoading.value = false;
          }, 2000);
        }
        break;
      case "ton":
        paymentStatus.value = t("message.creating_ton_order");
        let tonRes;
        console.log(funTON.value, params);

        tonRes = await funTON.value(params);
        if (tonRes.code === 200) {
          emitter.emit("showTonPayment", {
            data: tonRes,
            isBuy: isBuyMinigMachine.value,
            params,
            isMode: false,
          });
          isLoading.value = false;
          paymentStatus.value = "";
        } else {
          paymentStatus.value = t("message.create_order_failed");
          setTimeout(() => {
            paymentStatus.value = "";
            isLoading.value = false;
          }, 2000);
        }
        break;
      case "usdt":
        paymentStatus.value = t("message.creating_usdt_order");
        let usdtRes;
        emitter.emit("showNetworkModal", {
          funUSDT: funUSDT.value,
          amount: amount.value,
          params,
        });
        setTimeout(() => {
          paymentStatus.value = "";
          isLoading.value = false;
        }, 2000);
        break;
    }
  } catch (error) {
    console.error(error);
    paymentStatus.value = t("message.error");
    setTimeout(() => {
      paymentStatus.value = "";
      isLoading.value = false;
    }, 2000);
  } finally {
    isLoading.value = false;
  }
};

const handleClose = () => {
  visible.value = false;
  if (isMode.value) {
    emitter.emit("showPricing", true);
  }
};

const showPayment = (payAmount) => {
  console.log(payAmount);
  funStart.value = payAmount.funStart; // 星星订单
  funTON.value = payAmount.funTON; // TON订单
  funUSDT.value = payAmount.funUSDT; // USDT订单
  amount.value = payAmount.amount; // 金额
  params = payAmount.params; // 参数
  isBuyMinigMachine.value = payAmount.isBuy || ""; // 是否购买矿机
  visible.value = true;
};

onMounted(() => {
  emitter.on("showPayment", showPayment);
  emitter.on("closePayment", () => {
    visible.value = false;
  });
});
watch(
  () => themeColor.value,
  (newVal) => {
    document.documentElement.style.setProperty("--theme-color", newVal);
  }
);

onUnmounted(() => {
  emitter.off("showPayment", showPayment);
  emitter.off("closePayment", showPayment);
});
</script>

<style scoped>
:root {
  --theme-color: #00ff9d;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
  font-family: Arial, sans-serif;
}

.modal-content {
  background-color: #ffffff;
  border-radius: 4vw;
  width: 92vw;
  max-width: 600px;
  position: relative;
  box-shadow: 0 0.6vw 3vw rgba(0, 0, 0, 0.1);
}

.modal-header {
  padding: 4vw;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 0.3vw solid #e5e5e5;
}

.modal-title {
  font-size: 5.5vw;
  color: #333333;
  font-weight: bold;
}

.close-btn {
  font-size: 7vw;
  color: #999999;
  cursor: pointer;
  transition: all 0.3s ease;
}

.close-btn:hover {
  color: #333333;
}

.payment-options {
  padding: 4vw;
}

.payment-option {
  display: flex;
  align-items: center;
  padding: 4vw;
  background-color: #f5f5f5;
  border-radius: 3vw;
  margin-bottom: 3vw;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 0.4vw solid transparent;
}

.payment-option:last-child {
  margin-bottom: 0;
}

.payment-option.selected {
  border-color: var(--theme-color);
  background-color: #ffffff;
  box-shadow: 0 0 2vw rgba(0, 255, 157, 0.2);
}

.payment-icon {
  width: 10vw;
  height: 10vw;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 4vw;
}

.payment-icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.payment-name {
  flex: 1;
  font-size: 4vw;
  color: #333333;
}

.check-circle {
  width: 5vw;
  height: 5vw;
  border-radius: 50%;
  border: 0.5vw solid #cccccc;
  position: relative;
  transition: all 0.3s ease;
}

.check-circle.active {
  border-color: var(--theme-color);
  background-color: var(--theme-color);
  box-shadow: 0 0 1vw rgba(0, 255, 157, 0.4);
}

.check-circle.active::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 2vw;
  height: 2vw;
  background-color: #000000;
  border-radius: 50%;
}

.payment-footer {
  padding: 3vw;
  background-color: #f5f5f5;
  border-radius: 0 0 3vw 3vw;
}

.payment-amount {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3vw;
}

.payment-amount .label {
  font-size: 4vw;
  color: #666666;
}

.payment-amount .amount {
  font-size: 5vw;
  color: var(--theme-color);
  font-weight: bold;
}

.pay-btn {
  width: 100%;
  background-color: var(--theme-color);
  border: none;
  color: #fff;
  font-size: 4.5vw;
  font-weight: bold;
  padding: 4vw;
  border-radius: 2vw;
  cursor: pointer;
  transition: all 0.3s ease;
}

.pay-btn:hover {
  opacity: 0.9;
  transform: translateY(-0.2vw);
}

.pay-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.payment-status-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 3vw;
}

.payment-status-content {
  text-align: center;
  color: #333333;
  font-size: 4vw;
}

.loading-spinner {
  width: 10vw;
  height: 10vw;
  border: 0.8vw solid #e5e5e5;
  border-top-color: var(--theme-color);
  border-radius: 50%;
  animation: spin 1s infinite linear;
  margin: 0 auto 3vw;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* 动画 */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-scale-enter-active,
.modal-scale-leave-active {
  transition: all 0.3s ease;
}

.modal-scale-enter-from,
.modal-scale-leave-to {
  transform: scale(0.9);
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
